import { useEffect, useMemo, useRef } from "react";

import { pasteReservationSlot } from "../../interaction/pasteReservationAction";
import { COMMAND_TYPE } from "../sync/commandTypes";
import { createCalendarCommandQueue } from "../sync/commandQueue";
import { registerCalendarCommandQueue } from "../sync/commandQueueRegistry";
import { registerCalendarStore } from "../store/calendarStoreRegistry";
import { createCalendarStore } from "../store/calendarStore";
import {
   createReservationSlotResolver,
   hydrateCalendarStoreFromRows,
} from "../bridge/calendarStoreBridge";
import { getCurrentPopup } from "../../../components/Common/popupUIStore";

/**
 * Master Calendar engine: single store + professional command queue.
 * Bridges Redux month fetch into CalendarStore and exposes command dispatch.
 */
export function useMasterCalendarEngine({
   store: externalStore = null,
   currentMonthValue,
   liveReservations,
   /** Immutable month-query snapshot — preferred hydrate source (M2). */
   monthReservationSnapshotRows = null,
   monthReservationsData,
   dayIndexByKey,
   interactionCtxRef,
   helpers,
   lateralInstructorId,
   onCommandStatusChange,
   onCommandFailed,
}) {
   const storeRef = useRef(externalStore || null);
   if (externalStore) {
      storeRef.current = externalStore;
   } else if (!storeRef.current) {
      storeRef.current = createCalendarStore();
   }
   const store = storeRef.current;

   const slotResolver = useMemo(
      () =>
         createReservationSlotResolver({
            dayIndexByKey,
            lateralInstructorId,
            getReservationStartRaw: helpers?.getReservationStartRaw,
            extractYmdPrefix: helpers?.extractYmdPrefix,
            toFloatingDate: helpers?.toFloatingDate,
            formatDateKeyInChisinau: helpers?.formatDateKeyInChisinau,
            formatTimeLabelInChisinau: helpers?.formatTimeLabelInChisinau,
            normalizeHourMinuteLabel: helpers?.normalizeHourMinuteLabel,
            findNearestTimeSlotLabel: helpers?.findNearestTimeSlotLabel,
            normalizedTimeLabels: helpers?.normalizedTimeLabels,
            timeLabelMinutes: helpers?.timeLabelMinutes,
            getReservationColumnInstructorId:
               helpers?.getReservationColumnInstructorId,
         }),
      [dayIndexByKey, helpers, lateralInstructorId],
   );

   const commandQueueRef = useRef(null);
   if (!commandQueueRef.current) {
      commandQueueRef.current = createCalendarCommandQueue({
         onStatusChange: onCommandStatusChange,
         execute: async (command) => {
            const ctx = interactionCtxRef?.current;
            if (!ctx) {
               throw new Error("interaction-context-unavailable");
            }

            if (command.type === COMMAND_TYPE.PASTE) {
               const payload = command.payload || {};
               const { buildUndoEntry: _buildUndo, ...slotPayload } = payload;
               const snapshot = command.snapshot || null;
               if (!snapshot) throw new Error("paste-missing-snapshot");
               const prevBuffer = ctx.copyBufferRef?.current;
               ctx.copyBufferRef.current = snapshot;
               try {
                  const result = await pasteReservationSlot(
                     { ...ctx, activeCommandId: command.id },
                     slotPayload,
                  );
                  // Soft reject: paste already showed a clear alert. Do not throw
                  // the opaque "paste-rejected" that onCommandFailed surfaces.
                  if (!result?.ok) {
                     const softError = new Error(
                        String(result?.reason || "").trim() ||
                           "Lipirea a fost respinsă.",
                     );
                     softError.alreadyAlerted = result?.alreadyAlerted === true;
                     softError.softReject = true;
                     throw softError;
                  }
                  return result;
               } finally {
                  if (prevBuffer !== undefined) {
                     ctx.copyBufferRef.current = prevBuffer;
                  }
               }
            }

            if (
               command.type === COMMAND_TYPE.DELETE ||
               command.type === COMMAND_TYPE.CREATE ||
               command.type === COMMAND_TYPE.MOVE ||
               command.type === COMMAND_TYPE.RESIZE ||
               command.type === COMMAND_TYPE.BLOCK ||
               command.type === COMMAND_TYPE.UNBLOCK
            ) {
               if (typeof command.payload?.run === "function") {
                  return command.payload.run(command);
               }
            }

            throw new Error(`unsupported-command:${command.type}`);
         },
      });
   }
   const commandQueue = commandQueueRef.current;

   useEffect(() => {
      registerCalendarStore(store);
      return () => {
         registerCalendarStore(null);
      };
   }, [store]);

   useEffect(() => {
      registerCalendarCommandQueue(commandQueue);
      return () => {
         registerCalendarCommandQueue(null);
      };
   }, [commandQueue]);

   useEffect(() => {
      if (!currentMonthValue) return;
      store.resetMonth(currentMonthValue);
   }, [currentMonthValue, store]);

   const hydrateSourceKey = useMemo(() => {
      if (!currentMonthValue) return "";
      const serverTime =
         monthReservationsData?.serverTime ||
         monthReservationsData?.appliedAt ||
         "";
      const snapshotCount = Array.isArray(monthReservationSnapshotRows)
         ? monthReservationSnapshotRows.length
         : 0;
      return `${currentMonthValue}|${serverTime}|${snapshotCount}`;
   }, [
      currentMonthValue,
      monthReservationSnapshotRows,
      monthReservationsData?.appliedAt,
      monthReservationsData?.serverTime,
   ]);

   useEffect(() => {
      if (!currentMonthValue || !hydrateSourceKey) return undefined;
      const serverTime =
         monthReservationsData?.serverTime ||
         monthReservationsData?.appliedAt ||
         "";
      const items = Array.isArray(monthReservationSnapshotRows)
         ? monthReservationSnapshotRows
         : liveReservations;
      let cancelled = false;
      const popupOpen = Boolean(getCurrentPopup()?.type);
      const delayMs = popupOpen ? 160 : 48;
      const timerId = window.setTimeout(() => {
         if (cancelled) return;
         hydrateCalendarStoreFromRows(store, {
            monthKey: currentMonthValue,
            items,
            serverTime,
            slotResolver,
         });
      }, delayMs);
      return () => {
         cancelled = true;
         window.clearTimeout(timerId);
      };
   }, [
      currentMonthValue,
      hydrateSourceKey,
      liveReservations,
      monthReservationSnapshotRows,
      monthReservationsData?.appliedAt,
      monthReservationsData?.serverTime,
      slotResolver,
      store,
   ]);

   const dispatchCommand = useMemo(
      () => ({
         paste: (payload, snapshot, options) =>
            commandQueue
               .enqueue(COMMAND_TYPE.PASTE, payload, snapshot, options)
               .catch((error) => {
                  onCommandFailed?.(error);
                  throw error;
               }),
         run: (type, payload, snapshot, options) =>
            commandQueue.enqueue(type, payload, snapshot, options).catch((error) => {
               onCommandFailed?.(error);
               throw error;
            }),
      }),
      [commandQueue, onCommandFailed],
   );

   return {
      store,
      commandQueue,
      dispatchCommand,
      slotResolver,
      patchBus: store.patchBus,
      undo: () => commandQueue.undo(),
      redo: () => commandQueue.redo(),
      canUndo: () => commandQueue.canUndo(),
      canRedo: () => commandQueue.canRedo(),
   };
}
